import React from 'react';
import {connect, DispatchProp} from "react-redux";
import {RootState} from "../../../../../reducers";
import {getCart} from "../../../../services/Cart/reducer";
import {getSession, selectLocale} from "../../../../services/Session/reducer";
import {useTranslation, withTranslation} from "react-i18next";
import RoundedIcon from "../../../Common/RoundedIcon";
import {ReactComponent as DropdownIco} from "../../../../../images/icons/arrow-bottom.svg";
import {ReactComponent as PickUpIco} from "../../../../../images/icons/pick-up.svg";
import HoursHelper from "../../../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../../../services/Common/Date/DateService";
import {getStoreConfig} from "../../../../services/Store/reducer";
import {anyMenuHasPickDate} from "../../utils";
import AvailableHelper from "../../../../services/Menu/services/Availability/AvailableHelper";
import {DateHelper} from "../../../../services/Common/Date/DateHelper";
import {getAvailabilities, getMenus} from "../../../../services/Menu/reducer";

interface OwnProps {
    handleShowOrderDetailsModal: () => void;
    handleOnKeyDown: (event: (React.KeyboardEvent<HTMLDivElement> | KeyboardEvent)) => Promise<void>;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

const CartButtonPickUpInfo = ({
                                  config,
                                  session,
                                  locale,
                                  handleShowOrderDetailsModal,
                                  availabilities,
                                  menus,
                                  handleOnKeyDown
                              }: Props) => {
    const isStoreOpen = HoursHelper.isOpen(config?.hours, DateService.now());
    const {t} = useTranslation();
    const orderTypeMenuAvailabilities = AvailableHelper
        .getAvailabilities(availabilities, menus
            .map((menu: any) => menu.takeaway && menu.availability_id));

    const getInfoDetails = (): {
        topInfo: string | JSX.Element;
        topInfoColor: string;
        bottomInfo: string | JSX.Element
    } => {
        if (!session.pickup_at) {
            if (isStoreOpen) {
                const isOpenForCurrentType = HoursHelper.isOpen(orderTypeMenuAvailabilities.flatMap((availability: any) => availability.hours), DateService.now())
                    && orderTypeMenuAvailabilities.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.now()));

                if (isOpenForCurrentType) {
                    return {
                        topInfo: t("common.word.as_soon_as_possible").toLowerCase(),
                        topInfoColor: "text-success",
                        bottomInfo: '',
                    }
                } else {
                    const nearestOpenHours = AvailableHelper.getNearestOpenDateFromNowOrDate(orderTypeMenuAvailabilities)?.dateFrom;
                    const date = nearestOpenHours ? DateHelper.getFormattedNearestDate(DateHelper.format(nearestOpenHours), t, locale ?? "") : undefined;

                    let bottomInfo: JSX.Element = <></>;

                    if (config?.ordering?.pick_date) {
                        bottomInfo = <span
                            className="text-primary">{t("modules.cart.field.change_date_or_order_type.title")}.</span>
                    } else {
                        bottomInfo = date ? <span
                            className="text-primary">{t("modules.cart.field.change_order_type.title", {date})}</span> : <></>
                    }

                    return {
                        topInfo: t("modules.cart.field.not_available_now.title"),
                        topInfoColor: "text-danger",
                        bottomInfo
                    }
                }
            } else {
                const nearestOpenHours = AvailableHelper.getNearestOpenDateFromNowOrDate(orderTypeMenuAvailabilities)?.dateFrom;
                const date = nearestOpenHours ? DateHelper.getFormattedNearestDate(DateHelper.format(nearestOpenHours), t, locale ?? "") : undefined;
                const pickDateAvailable = anyMenuHasPickDate(menus, "takeaway");
                const topInfo = t("modules.cart.field.closed_now.title");
                const topInfoColor = "text-danger";

                if (pickDateAvailable) {
                    return {
                        topInfo,
                        topInfoColor,
                        bottomInfo: <span
                            className="text-primary">{`${t("modules.cart.field.change_date_to_order.title")}.`}</span>
                    }
                }

                return {
                    topInfo,
                    topInfoColor,
                    bottomInfo: <span className="text-muted">{t("modules.cart.field.open_at.title", {date})}</span>
                }
            }
        } else {
            return {
                topInfo: DateHelper.getFormattedNearestDate(session.pickup_at, t, locale ?? ""),
                topInfoColor: "text-success",
                bottomInfo: '',
            };
        }
    }

    const drawFullInfo = () => {
        const infoDetails = getInfoDetails();

        return (
            <>
                <div>
                    <span className="font-weight-bold">{`${t("enums.order.type.PICK_UP")}: `}</span>
                    <span className={`${infoDetails?.topInfoColor} font-weight-bold`}>{infoDetails?.topInfo}</span>
                </div>
                <div>{infoDetails?.bottomInfo}</div>
            </>
        )
    }

    return (
        <div className="cart-button-info" onClick={handleShowOrderDetailsModal} tabIndex={0} role="button"
             onKeyDown={handleOnKeyDown}>
            <RoundedIcon icon={<PickUpIco/>} size="medium" className="text-primary"/>
            <div className="cart-button-info-middle-box">
                {drawFullInfo()}
            </div>
            <DropdownIco className="ms-auto dropdown-ico"/>
        </div>
    )
}

const mapStateToProps = (state: RootState) => ({
    cart: getCart(state.cart),
    session: getSession(state.session),
    config: getStoreConfig(state.config),
    availabilities: getAvailabilities(state.menu),
    menus: getMenus(state.menu),
    locale: selectLocale(state.session),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartButtonPickUpInfo));